import { useState, useTransition } from "react";
import type { BriefField } from "@excellent/core";
import { FormField } from "../form-shell";
import {
  BriefFieldInput,
  defaultForType,
  equalValues,
} from "./brief-field-input";
import { ProvenanceRow, type BriefFieldMeta } from "./provenance-row";

export interface BriefFieldRowProps {
  field: BriefField;
  initial: unknown;
  /** Save one field. Called on blur (text) or change (select). */
  onSave: (key: string, value: unknown) => Promise<void>;
  /** Focus the input on mount. */
  autofocus?: boolean;
  /** Optional auto-fill provenance for this field (renders the footer row). */
  meta?: BriefFieldMeta | null;
  onConfirm?: (key: string) => Promise<void>;
  onReject?: (key: string) => Promise<void>;
  className?: string;
}

/**
 * The one labeled brief field — the canonical presentation for an
 * inline-editable field. Built on the shared `<FormField>` atom so a section
 * brief is presented with the exact same vocabulary as every other app form
 * (concise label-led headline, required = red dot, one `type-subtext` help line).
 * See docs/design/FORM-SYSTEM.md.
 *
 * Auto-saves on blur (text) / change (select). The inline save status rides
 * FormField's `labelAction` slot; any auto-fill provenance rides its `footer`
 * slot. The conversational question (`field.prompt`) is deliberately NOT shown
 * here — it is the headline of the one-at-a-time guided walk (BriefSetupDriver),
 * where leading with the question is the right UX. Both surfaces share
 * `BriefFieldInput`, so the select/multi/number editors behave identically.
 */
export function BriefFieldRow({
  field,
  initial,
  onSave,
  autofocus = false,
  meta = null,
  onConfirm,
  onReject,
  className,
}: BriefFieldRowProps) {
  const [value, setValue] = useState<unknown>(initial ?? defaultForType(field));
  const [lastSaved, setLastSaved] = useState<unknown>(
    initial ?? defaultForType(field),
  );
  const [pending, startTransition] = useTransition();
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dirty = !equalValues(value, lastSaved);

  function commit() {
    if (!dirty) return;
    setError(null);
    const next = value;
    startTransition(async () => {
      try {
        await onSave(field.key, next);
        setLastSaved(next);
        setSavedAt(new Date().toLocaleTimeString());
      } catch (err) {
        setError(err instanceof Error ? err.message : "Save failed");
      }
    });
  }

  // Quiet by default: an untouched, clean field shows no status at all so the
  // form stays scannable. Status only appears once the user engages.
  const statusText = pending
    ? "Saving…"
    : dirty
      ? "Unsaved"
      : savedAt
        ? `Saved ${savedAt}`
        : null;

  // The subhead is the explanatory line that makes the field answerable; skip
  // it when it merely repeats the label (some specs leave it unset).
  const help =
    field.subhead && field.subhead !== field.label ? field.subhead : undefined;

  return (
    <FormField
      className={className}
      label={field.label}
      required={field.required}
      help={help}
      error={error ?? undefined}
      labelAction={
        statusText ? (
          <span
            className="type-subtext text-muted-foreground tabular-nums"
            aria-live="polite"
          >
            {statusText}
          </span>
        ) : undefined
      }
      footer={
        meta ? (
          <ProvenanceRow
            fieldKey={field.key}
            meta={meta}
            onConfirm={onConfirm}
            onReject={onReject}
          />
        ) : undefined
      }
    >
      <BriefFieldInput
        field={field}
        value={value}
        onChange={setValue}
        onCommit={commit}
        disabled={pending}
        autofocus={autofocus}
      />
    </FormField>
  );
}

export type { BriefFieldMeta };
